import { FontAwesome5 } from '@expo/vector-icons';
import {
  secondaryFontFamily,
  styled,
  ThemeType,
  css,
} from '@openeducation/oe-rn-themes';
import React, { memo, useMemo } from 'react';

import { OEText } from '../../atoms/oe-text/oe-text';
import TooltipBallon from '../../atoms/tooltip-balloon/tooltip-balloon';
import { left, top, width } from '../../style-utils';

export interface LevelProgressBarItemProps {
  levelName: string;
  isActive?: boolean;
  isFinished?: boolean;
  isLast?: boolean;
  isFirst?: boolean;
  progressPercent?: number;
}

const BAR_WIDTH = 70;
const BAR_HEIGHT = 8;
const PLANE_SIZE = 15;
const TOOLTIP_WIDTH = 53;

export const LevelProgressBarItem = ({
  levelName,
  progressPercent,
  isActive,
  isFinished,
  isLast,
  isFirst,
}: LevelProgressBarItemProps) => {
  const progress = Math.round(progressPercent || 0);
  const barWidth = progress ? Math.floor((BAR_WIDTH * progress) / 100) : 0;

  const leftPosition = useMemo(() => {
    return barWidth - TOOLTIP_WIDTH / 2;
  }, [barWidth]);
  return (
    <Container isLast={isLast}>
      <Bar isFinished={isFinished} isFirst={isFirst} isLast={isLast}>
        {isActive && (
          <>
            <TooltipBallon
              progressPercent={progress}
              style={[top(-42), left(leftPosition), width(TOOLTIP_WIDTH)]}
            />
            <InnerBar barWidth={barWidth} isFirst={isFirst} isLast={isLast}>
              <PlaneIconWrapper barWidth={barWidth}>
                <PlaneBackground />
                <PlaneIcon />
              </PlaneIconWrapper>
            </InnerBar>
          </>
        )}
      </Bar>
      <LevelName isActive={isActive} isFinished={isFinished}>
        {levelName}
      </LevelName>
    </Container>
  );
};

type ContainerType = {
  isLast?: boolean;
};

const Container = styled.View<ContainerType>`
  margin-right: ${({ isLast }) => (isLast ? 0 : 10)}px;
  width: ${BAR_WIDTH}px;
  flex: 1;
  align-items: center;
`;

type BorderRadiusType = {
  isFirst?: boolean;
  isLast?: boolean;
};

const BAR_BORDER_RADIUS = BAR_HEIGHT / 2;

const borderRadiusStyle = css<BorderRadiusType>`
  border-top-left-radius: ${({ isFirst }) =>
    isFirst ? BAR_BORDER_RADIUS : 0}px;
  border-bottom-left-radius: ${({ isFirst }) =>
    isFirst ? BAR_BORDER_RADIUS : 0}px;
  border-top-right-radius: ${({ isLast }) =>
    isLast ? BAR_BORDER_RADIUS : 0}px;
  border-bottom-right-radius: ${({ isLast }) =>
    isLast ? BAR_BORDER_RADIUS : 0}px;
`;

type BarType = {
  isFinished?: boolean;
} & BorderRadiusType;

const Bar = styled.View<BarType>`
  height: ${BAR_HEIGHT}px;
  width: ${BAR_WIDTH}px;
  background-color: ${({ theme, isFinished }) =>
    isFinished ? theme.color.accentDark : theme.color.border};
  margin-bottom: 8px;
  ${borderRadiusStyle};
`;

type InnerBarType = {
  barWidth: number;
} & BorderRadiusType;

const InnerBar = styled.View<InnerBarType>`
  background-color: ${({ theme }) => theme.color.accentDark};
  height: ${BAR_HEIGHT}px;
  width: ${({ barWidth }) => barWidth}px;
  border-radius: ${BAR_HEIGHT / 2}px;
  ${borderRadiusStyle};
`;

type LevelNameType = {
  theme: ThemeType;
  isFinished?: boolean;
  isActive?: boolean;
};

const LevelName = styled(OEText).attrs(
  ({ theme, isActive, isFinished }: LevelNameType) => ({
    fontFamily: secondaryFontFamily,
    fontType: isActive ? 'semiBold' : 'regular',
    color: isFinished || isActive ? theme.color.black : theme.color.dark3,
    fontSize: 12,
  })
)<LevelNameType>``;

type PlaneIconWrapperType = {
  barWidth: number;
};

const PlaneIconWrapper = styled.View<PlaneIconWrapperType>`
  position: absolute;
  left: ${({ barWidth }) => barWidth - PLANE_SIZE / 2}px;
`;

const PlaneBackground = styled.View`
  position: absolute;
  background-color: ${({ theme }) => theme.color.background};
  height: ${BAR_HEIGHT}px;
  width: ${PLANE_SIZE + 6}px;
  top: 0;
  margin-left: -3px;
`;

const PlaneIcon = styled(FontAwesome5).attrs(({ theme }) => ({
  color: theme.color.primary,
  name: 'plane',
  size: PLANE_SIZE,
}))`
  position: absolute;
  top: -3px;
`;

export default memo(LevelProgressBarItem);
